import { Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Alert } from 'src/app/interfaces/alert';
import {
  Pet,
  PetMissingReportStatus,
  PetResidenceStatus,
  PetSpeciesFilterableType
} from 'src/app/interfaces/pet';
import { User, ProductType } from 'src/app/interfaces/user';
import { UserService } from 'src/app/services/user/user.service';
import { Perk, WidgetSize } from 'src/app/interfaces/perk';
import { environment } from 'src/environments/environment';
import { CheckoutService } from 'src/app/services/checkout/checkout.service';
import { HelperService } from 'src/app/shared/helpers/helper.service';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { UpdatePetDetailsInfoModalComponent } from '../update-pet-details-info-modal/update-pet-details-info-modal.component';
import { SCROLL_TO_OFFERS_QUERYPARAM } from 'src/app/constants/app.constants';
import { PricingTableSubTypes } from 'src/app/shared/components/pricing-table/pricing-table.component';
import { FinishWelcomeGiftStepModalComponent } from '../finish-welcome-gift-step-modal/finish-welcome-gift-step-modal.component';
import {
  RegistrationProgressData,
  RegistrationProgressService,
} from 'src/app/services/registration-progress/registration-progress.service';
import { MemberShipOptionsService } from 'src/app/services/membership-options/membership-options.service';
import { FollowUsModalComponent } from 'src/app/modals/follow-us-modal/follow-us-modal.component';
import { PricingInfoLine } from 'src/app/shared/components/pricing-table/price-card/price-card.component';
import * as UAParser from 'ua-parser-js';
import { TokExploreModalComponent } from '../../modals/tok-explore-modal/tok-explore-modal.component';
import { TokCompleteModalComponent } from '../../modals/tok-complete-modal/tok-complete-modal.component';
import { CloudinaryService } from 'src/app/services/cloudinary/cloudinary.service';
import { ClickEventNumbersEnum } from 'src/app/interfaces/clickEventNumbers';
import { isPlatformBrowser } from '@angular/common';
import { OptInPopupService } from 'src/app/services/opt-in-popup/opt-in-popup.service';
import { Subscription } from 'rxjs';
import { NationalMicrochippingMonthPopupService } from 'src/app/services/national-microchipping-month-popup/national-microchipping-month-popup.service';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
})
export class DashboardComponent implements OnInit {
  alert: Alert = null;
  optInAlert: Alert = null;
  activePets: Array<Pet> = [];
  incompletePetRegistations: Pet[] = [];
  loading = false;
  perks: Array<Perk> = [];
  petListString: string;
  welcomeMessage: string;
  previewPremium: boolean = null;
  productType = ProductType;
  public user: User;
  missingPetsWithNoReport: Pet[];
  tokPets: Pet[];
  petResidenceStatus = PetResidenceStatus;
  pitPatAvailable: boolean = true;
  qrcode: string;
  pitPatPricingInfoLines: PricingInfoLine[] = [
    {
      priceAmount: '£129',
      priceDescription: 'for identibase+, identialert and identifind plan holders',
    },
    { priceAmount: '£159', priceDescription: 'for Basic plan holders' },
  ];
  pitPatProductId: string;
  ClickEventNumbersEnum = ClickEventNumbersEnum;

  public PricingTableSubTypes = PricingTableSubTypes;

  public pricingPlans = environment.stripePlans;
  public alivePets: Array<Pet> = [];
  public isFullNameRegistrationCompleted = true;
  public isPetChipNumberRegistrationCompleted = true;
  public isMembershipSelectionCompleted = true;
  public isWelcomePackSelectCompleted = true;
  public WidgetSize = WidgetSize;
  public doesTheUserHaveCat = false;
  public doesTheUserHaveDog = false;
  public haveMembershipOptons = this.doesHaveAvailableMemberShipOptions();
  public isDeviceTypeMobile: boolean;
  private popupSeenSub: Subscription;

  // Toggle these variables to manage popup active status
  // true: modal is turned on
  // false: modal is turned off
  private showOptInPopup: boolean = false;
  private showNationalMicrochippingMonthPopup: boolean = true;

  // Control instagram popup behaviour
  private readonly showInstagramPopup: boolean = false;

  choosePetModalRef?: NgbModalRef;
  isBrowser: boolean;

  constructor(
    private route: ActivatedRoute,
    public userService: UserService,
    private _router: Router,
    private checkoutService: CheckoutService,
    private _helperService: HelperService,
    private _modalService: NgbModal,
    private registrationProgressService: RegistrationProgressService,
    private membershipOptionsService: MemberShipOptionsService,
    private cloudinaryService: CloudinaryService,
    private optInPopupService: OptInPopupService,
    private nationalMicroChippingMonthPopupService: NationalMicrochippingMonthPopupService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    this.user = this.route.snapshot.data.user as User;
    this.perks = this.route.snapshot.data.perks as Array<Perk>;
    this.activePets = this.userService.filterAliveRegisteredPets(this.user.pets);
    this.petListString = this.createPetsListString(this.activePets);
    this.pitPatProductId = this.pricingPlans.pitPatGpsTracker;
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  closeAlert() {
    this.alert = null;
  }
  closeOptInAlert() {
    this.optInAlert = null;
  }

  navigateToClaimPetOrEditPet() {
    if (this.user.pets.length > 0) {
      const incompletePets = this.user.pets.filter(
        (p) => p.name === null || p.name === '[to_be_completed_by_keeper]'
      );
      if (incompletePets.length > 0) {
        this._router.navigate(['/my-pet', incompletePets[0].id]);
      } else {
        // All of the previous pets details have been filled
        this._router.navigate(['/claim-pet'], {
          queryParams: { r: 'dashboard' },
        });
      }
    } else {
      // The user has not claimed any pets before
      this._router.navigate(['/claim-pet'], {
        queryParams: { r: 'dashboard' },
      });
    }
  }

  private createWelcomeMessage(): string {
    const startText = this.doesHaveAvailableMemberShipOptions()
      ? 'Check out your membership plans and products for'
      : 'Check out your perks for';
    if (!!this.user.pets && !!this.petListString) {
      return `${startText} ${this.petListString}`;
    }

    return `${startText} your pets`;
  }

  private doesHaveAvailableMemberShipOptions(): boolean {
    const hasIdentibasePlusOption = this.membershipOptionsService.identiBasePlusAvailable(
      this.user
    );
    const hasIdentiAlertOption = this.membershipOptionsService.identiAlertAvailable(this.user);
    const hasIdentiFindOption = this.membershipOptionsService.identiFindAvailable(this.user);

    return hasIdentiAlertOption || hasIdentiFindOption || hasIdentibasePlusOption;
  }

  private createPetsListString(pets): string {
    if (!pets.length) {
      return '';
    } else if (pets.length === 1) {
      return pets[0].name;
    } else if (pets.length === 2) {
      return `${pets[0].name} & ${pets[1].name}`;
    } else if (pets.length === 3) {
      return `${pets[0].name}, ${pets[1].name} & ${pets[2].name}`;
    } else {
      return `${pets[0].name}, ${pets[1].name} & your other pets`;
    }
  }

  private getMissingPetsWithNoReport(pets: Pet[]): Pet[] {
    return pets.filter((pet) => {
      return (
        (pet.residence === PetResidenceStatus.Lost ||
          pet.residence === PetResidenceStatus.Stolen) &&
        !pet.missingPetReports.find(
          (report) =>
            report.reportStatus === PetMissingReportStatus.AwaitingModeration ||
            report.reportStatus === PetMissingReportStatus.Open ||
            report.reportStatus === PetMissingReportStatus.AwaitingClosureFound ||
            report.reportStatus === PetMissingReportStatus.AwaitingClosureDeceased
        )
      );
    });
  }

  trackEvent(event: string) {
    if (!this.isBrowser) return;
    window.analytics.track(event);
  }

  ngOnInit(): void {

    // Removing the petplan object from the perks list.
    this.perks = this.perks.filter(perk => perk.name !== 'Petplan');

    if (
      this.isBrowser &&
      this.registrationProgressService.getWelcomeGiftCompletedState(this.user)
    ) {
      localStorage.removeItem("arrived_from_email");
    }

    const parser = new UAParser();
    this.isDeviceTypeMobile = parser.getResult().device.type === 'mobile';

    if (environment.name === 'development' || environment.name === 'local-dev') {
      this.qrcode = '/assets/images/home/DEVmobile.svg';
    } else if (environment.name === 'test') {
      this.qrcode = '/assets/images/home/TESTmobile.svg';
    } else if (environment.name === 'production') {
      this.qrcode = '/assets/images/home/PRODmobile.svg';
    } else if (environment.name === 'local') {
      this.qrcode = '/assets/images/home/DEVmobile.svg';
    }
    if (
      this.route.snapshot.queryParams.checkout &&
      this.route.snapshot.queryParams.checkout === 'completed'
    ) {
      this.alert = {
        type: 'success',
        message: `Your payment has been received and processed. Your account will be upgraded shortly, please try refreshing your browser to see your new pet perks and update your account details.`,
      };
    }

    if (
      this.route.snapshot.queryParams.followUs &&
      this.route.snapshot.queryParams.followUs === 'open' &&
      this.showInstagramPopup
    ) {
      this.openInstagramPopup();
    }

    if (this.user) {
      this.doesTheUserHaveCat = !!this.user.pets.find(
        (pet) => pet.speciesFilter === PetSpeciesFilterableType.Cat
      );
      this.doesTheUserHaveDog = !!this.user.pets.find(
        (pet) => pet.speciesFilter === PetSpeciesFilterableType.Dog
      );
    }

    this.tokPets = this.userService.filterTokPets(this.user.pets);
    if (this.tokPets.length !== 0) {
      this.openTokCompleteModal();
    }

    if (
      this.route.snapshot.queryParams.tokExplore &&
      this.route.snapshot.queryParams.tokExplore === 'open'
    ) {
      this.openTokExploreModal();
    }

    this.welcomeMessage = this.createWelcomeMessage();

    this.incompletePetRegistations = this.userService.filterIncompletePetRegistrations(
      this.user.pets
    );
    this.missingPetsWithNoReport = this.userService.isUserBasicOrBasicPlus(this.user.productType)
      ? []
      : this.getMissingPetsWithNoReport(this.activePets);

    const progressData = this.registrationProgressService.getRegistrationProgressData(this.user);
    this.handleRegistrationProgression(progressData);

    // Uncomment if you want to show the welcomeGiftStep modal!
    // this.handlePremiumUsersWithWelcomeGiftStepAsLeftover();

    this.alivePets = this.userService.filterAlivePets(this.user.pets);

    if (this.showOptInPopup) {
      this.optInPopupService.checkAndShowNewsletterPopup(this.petListString, this.user);
  
      this.popupSeenSub = this.optInPopupService.popupSeenSuccess$.subscribe(() => {
        this.optInAlert = {
          type: 'success',
          message: 'Communication preferences saved.',
        };
      });
    }

    if (this.showNationalMicrochippingMonthPopup) {
      if(this.doesTheUserHaveCat && this.doesTheUserHaveDog) {
        // Has both, determine which comes first in the pet list
        const petType = this.getFirstCatOrDog(this.user.pets);
  
        if (petType) {
          this.nationalMicroChippingMonthPopupService.checkAndShowNationalMicrochippingMonthPopup(petType, this.user);
        }
      } else if (this.doesTheUserHaveCat) {
        // Has only cat
        this.nationalMicroChippingMonthPopupService.checkAndShowNationalMicrochippingMonthPopup('cat', this.user);
      } else if (this.doesTheUserHaveDog) {
        // Has only dog
        this.nationalMicroChippingMonthPopupService.checkAndShowNationalMicrochippingMonthPopup('dog', this.user);
      }
    }
  }

  ngOnDestroy(): void {
    this.popupSeenSub?.unsubscribe();
  }

  select(planId?: string): void {
    if (planId) {
      this.loading = true;
      this.checkoutService
        .checkout(this.planIdCheck(planId))
        .subscribe(
          () => {
            // Nothing should happen here because if checkout gets completed successfully, a redirect
            // will be triggered by stripe checkout
          },
          (err) => {
            this.alert = {
              type: 'danger',
              message: 'An error has occurred. Please try again later.',
            };
          }
        )
        .add(() => {
          this.loading = false;
        });
    }
  }

  public planIdCheck(planId: string) {
    if (
      planId === environment.stripePlans.identiAlertFind ||
      planId === environment.stripePlans.pitPatGpsTracker ||
      planId === environment.stripePlans.pitPatGpsTrackerBasic
    ) {
      if (this.userService.isUserAlertOrFind(this.user.productType)) {
        return [environment.stripePlans.pitPatGpsTracker];
      } 
      if (
        planId === environment.stripePlans.pitPatGpsTracker ||
        planId === environment.stripePlans.pitPatGpsTrackerBasic
      ){
        if (this.userService.isUserBasic(this.user.productType)) {
          return [environment.stripePlans.pitPatGpsTrackerBasic];
        }
        return [environment.stripePlans.pitPatGpsTracker];
      } 
      return [environment.stripePlans.pitPatGpsTracker, environment.stripePlans.identiAlertFind];
    } 
    return [planId];
  }

  public navigateToProductPage(pricingTableSubType: string): void {
    switch (pricingTableSubType) {
      case PricingTableSubTypes.IDENTIBASEPLUS:
        this._router.navigate(['/identibase-plus']);
        break;
      case PricingTableSubTypes.IDENTIALERT:
        this._router.navigate(['/identialert']);
        break;
      case PricingTableSubTypes.IDENTIFIND:
        this._router.navigate(['/identifind']);
        break;
      case PricingTableSubTypes.PitPat:
        this._router.navigate(['/pit-pat']);
        break;
    }
  }

  public scrollToOffers() {
    this._helperService.scrollToElementById('offerListHeader');
  }

  public openUpdatePetDetailsInfoModal() {
    this._modalService.open(UpdatePetDetailsInfoModalComponent);
  }

  public areTheInitialRegistrationStepsIncomplete() {
    return (
      !this.isFullNameRegistrationCompleted ||
      !this.isPetChipNumberRegistrationCompleted ||
      !this.isMembershipSelectionCompleted ||
      !this.isWelcomePackSelectCompleted
    );
  }

  public onLastPerkRendered() {
    if (this.route.snapshot.queryParams.scrollTo) {
      if (this.route.snapshot.queryParams.scrollTo === SCROLL_TO_OFFERS_QUERYPARAM) {
        this.scrollToOffers();
      }
    }
  }

  private handleRegistrationProgression(progressData: RegistrationProgressData) {
    this.isFullNameRegistrationCompleted = progressData.isFullNameRegistrationCompleted;
    this.isMembershipSelectionCompleted = progressData.isMembershipSelectionCompleted;
    this.isPetChipNumberRegistrationCompleted = progressData.isPetChipNumberRegistrationCompleted;
    this.isWelcomePackSelectCompleted = progressData.isWelcomePackSelectCompleted;
  }

  private handlePremiumUsersWithWelcomeGiftStepAsLeftover() {
    if (!this.isWelcomePackSelectCompleted && this.doesUserHaveProductSub()) {
      this._modalService.open(FinishWelcomeGiftStepModalComponent);
    }
  }

  private openInstagramPopup() {
    if (!this.areTheInitialRegistrationStepsIncomplete()) {
      this._modalService.open(FollowUsModalComponent);
    }
  }

  private openTokExploreModal() {
    this._modalService.open(TokExploreModalComponent, {
      size: 'xl',
    });
  }

  private openTokCompleteModal() {
    this._modalService.open(TokCompleteModalComponent, {
      size: 'xl',
    });
  }

  private doesUserHaveProductSub() {
    return (
      this.user.productType === this.productType.BasicPlus ||
      this.user.productType === this.productType.Premium
    );
  }

  public getPetImageUrl(petImageId: string): string {
    return this.cloudinaryService.getImageUrl(petImageId, 225, 225, '/assets/images/placeholders/paw-square-image.png');
  }

  public navigateToPetDetailPage(petId: string) {
    return `/my-pet/${petId}`;
  }

  public getProgressData() {
    return this.registrationProgressService.getRegistrationProgressData(this.user);
  }

  private getFirstCatOrDog(userPets: Pet[]): 'cat' | 'dog' | null {
    for (const pet of userPets) {
      if (pet.speciesFilter === PetSpeciesFilterableType.Cat) {
        return 'cat';
      }
      if (pet.speciesFilter === PetSpeciesFilterableType.Dog) {
        return 'dog';
      }
    }
  return null;
  }
}
